import React, { useContext, useState, useMemo } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { DashboardContext } from "../contexts/DashboardContext";
import EmployeeProfile from "./EmployeeProfile";

export default function EmployeeDirectory() {
  const { employeesData } = useContext(DashboardContext);

  // Filter & Sort states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("All Roles");
  const [selectedStatus, setSelectedStatus] = useState("All Status");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Pagination State
  const [localPage, setLocalPage] = useState(1);
  const itemsPerPage = 5;

  // UI States
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Dynamically extract unique dropdown options from the actual data
  const uniqueRoles = useMemo(
    () => [
      ...new Set((employeesData || []).map((e) => e.remarks).filter(Boolean)),
    ],
    [employeesData],
  );

  const uniqueStatuses = useMemo(
    () => [
      ...new Set((employeesData || []).map((e) => e.status).filter(Boolean)),
    ],
    [employeesData],
  );

  // Sorting Handler
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
    );
  };

  // Memoized Filtering and Sorting Logic
  const processedEmployees = useMemo(() => {
    let processableData = [...(employeesData || [])];

    // 1. FILTERING
    processableData = processableData.filter((emp) => {
      const searchLower = searchTerm.toLowerCase();
      const name = (emp.full_name || "").toLowerCase();
      const email = (
        emp.official_email ||
        emp.personal_email ||
        ""
      ).toLowerCase();
      const id = (emp.employee_code || "").toLowerCase();
      const role = (emp.remarks || "").toLowerCase();
      const status = (emp.status || "").toLowerCase();

      const matchesSearch =
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        id.includes(searchLower);
      const matchesRole =
        selectedRole === "All Roles" || role === selectedRole.toLowerCase();
      const matchesStatus =
        selectedStatus === "All Status" ||
        status === selectedStatus.toLowerCase();

      return matchesSearch && matchesRole && matchesStatus;
    });

    // 2. SORTING
    if (sortConfig.key !== null) {
      processableData.sort((a, b) => {
        let aValue = a[sortConfig.key] || "";
        let bValue = b[sortConfig.key] || "";
        if (typeof aValue === "string") aValue = aValue.toLowerCase();
        if (typeof bValue === "string") bValue = bValue.toLowerCase();

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return processableData;
  }, [employeesData, searchTerm, selectedRole, selectedStatus, sortConfig]);

  // Pagination Logic (Client-Side for 5 rows per page)
  const totalLocalPages = Math.ceil(processedEmployees.length / itemsPerPage);
  const currentTableData = processedEmployees.slice(
    (localPage - 1) * itemsPerPage,
    localPage * itemsPerPage,
  );

  // Generate Page Numbers for Pagination
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalLocalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <>
      {/* Main Table Container - Removed overflow-hidden so drawer doesn't get trapped */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] mt-8 w-full relative transition-all duration-300">
        {/* Header & Filters */}
        <div className="p-5 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 border-b border-slate-100/80">
          <h3 className="text-[15px] font-semibold text-slate-800 tracking-tight shrink-0">
            Employee Directory
          </h3>

          <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
            {/* Search Bar */}
            <div className="relative flex items-center flex-1 sm:w-[280px]">
              <Search className="absolute left-3 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setLocalPage(1);
                }}
                className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200/80 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-slate-700 placeholder-slate-400 bg-slate-50/50 transition-all"
              />
            </div>

            {/* Role Filter (Dynamic) */}
            <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setLocalPage(1);
              }}
              className="px-4 py-2 text-xs border border-slate-200/80 rounded-xl bg-slate-50/50 text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 cursor-pointer appearance-none transition-all"
            >
              <option value="All Roles">All Roles</option>
              {uniqueRoles.map((role, i) => (
                <option key={i} value={role.toLowerCase()}>
                  {role}
                </option>
              ))}
            </select>

            {/* Status Filter (Dynamic) */}
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setLocalPage(1);
              }}
              className="px-4 py-2 text-xs border border-slate-200/80 rounded-xl bg-slate-50/50 text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 cursor-pointer appearance-none transition-all"
            >
              <option value="All Status">All Status</option>
              {uniqueStatuses.map((stat, i) => (
                <option key={i} value={stat.toLowerCase()}>
                  {stat.charAt(0).toUpperCase() + stat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table Section */}
        <div className="w-full overflow-x-auto min-h-[350px]">
          <table className="w-full text-left border-collapse min-w-[950px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/30 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                <th
                  className="py-4 px-6 cursor-pointer hover:text-slate-600 transition-colors group"
                  onClick={() => requestSort("full_name")}
                >
                  <div className="flex items-center">
                    Employee {getSortIcon("full_name")}
                  </div>
                </th>
                <th
                  className="py-4 px-4 cursor-pointer hover:text-slate-600 transition-colors group"
                  onClick={() => requestSort("employee_code")}
                >
                  <div className="flex items-center">
                    Employee ID {getSortIcon("employee_code")}
                  </div>
                </th>
                <th
                  className="py-4 px-4 cursor-pointer hover:text-slate-600 transition-colors group"
                  onClick={() => requestSort("remarks")}
                >
                  <div className="flex items-center">
                    Role {getSortIcon("remarks")}
                  </div>
                </th>
                <th className="py-4 px-4">Phone</th>
                <th
                  className="py-4 px-4 cursor-pointer hover:text-slate-600 transition-colors group"
                  onClick={() => requestSort("gender")}
                >
                  <div className="flex items-center">
                    Gender {getSortIcon("gender")}
                  </div>
                </th>
                <th
                  className="py-4 px-4 cursor-pointer hover:text-slate-600 transition-colors group"
                  onClick={() => requestSort("status")}
                >
                  <div className="flex items-center">
                    Status {getSortIcon("status")}
                  </div>
                </th>
                <th
                  className="py-4 px-4 cursor-pointer hover:text-slate-600 transition-colors group"
                  onClick={() => requestSort("created_at")}
                >
                  <div className="flex items-center">
                    Join Date {getSortIcon("created_at")}
                  </div>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50 text-xs text-slate-600">
              {currentTableData.length > 0 ? (
                currentTableData.map((emp, index) => (
                  <tr
                    key={emp.uuid || index}
                    onClick={() => setSelectedEmployee(emp)}
                    className="hover:bg-slate-50/60 transition-all duration-200 cursor-pointer"
                  >
                    <td className="py-3 px-6 flex items-center gap-4">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-tr from-blue-50 to-slate-100 text-blue-600 font-bold shrink-0 border border-slate-200/60 shadow-sm">
                        {emp.full_name
                          ? emp.full_name.charAt(0).toUpperCase()
                          : "U"}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">
                          {emp.full_name || "N/A"}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate mt-0.5">
                          {emp.official_email || "N/A"}
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4 font-medium text-slate-700 whitespace-nowrap">
                      {emp.employee_code || "N/A"}
                    </td>

                    <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                      {emp.remarks || "N/A"}
                    </td>

                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap font-mono text-[11px]">
                      {emp.primary_phone || "N/A"}
                    </td>

                    <td className="py-3 px-4 text-slate-600 whitespace-nowrap capitalize">
                      {emp.gender || "N/A"}
                    </td>

                    <td className="py-3 px-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                          (emp.status || "").toLowerCase() === "active"
                            ? "bg-emerald-50 text-emerald-600 border border-emerald-100/50"
                            : "bg-amber-50 text-amber-600 border border-amber-100/50"
                        }`}
                      >
                        {emp.status || "N/A"}
                      </span>
                    </td>

                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                      {emp.created_at
                        ? new Date(emp.created_at).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "N/A"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="py-16 text-center text-slate-400 text-xs"
                  >
                    <div className="flex flex-col items-center justify-center gap-3 opacity-60">
                      <Search className="w-8 h-8 text-slate-300" />
                      <p className="text-sm">
                        No employees found matching your criteria.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 px-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-100/80 text-[11px] text-slate-500 bg-slate-50/30">
          <div>
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {processedEmployees.length === 0
                ? 0
                : (localPage - 1) * itemsPerPage + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-slate-700">
              {Math.min(localPage * itemsPerPage, processedEmployees.length)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-700">
              {processedEmployees.length}
            </span>{" "}
            entries
          </div>

          <div className="flex items-center gap-1.5 flex-wrap justify-center">
            <button
              onClick={() => setLocalPage((prev) => Math.max(prev - 1, 1))}
              disabled={localPage === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-200/50 text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {getPageNumbers().map((number) => (
              <button
                key={number}
                onClick={() => setLocalPage(number)}
                className={`w-7 h-7 flex items-center justify-center rounded-lg font-medium transition-all duration-200 ${
                  localPage === number
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                    : "text-slate-600 hover:bg-slate-200/50"
                }`}
              >
                {number}
              </button>
            ))}

            <button
              onClick={() =>
                setLocalPage((prev) => Math.min(prev + 1, totalLocalPages || 1))
              }
              disabled={localPage === (totalLocalPages || 1)}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-200/50 text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Embedded Component Profile Drawer - Placed entirely outside the table container */}
      {selectedEmployee && (
        <EmployeeProfile
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      )}
    </>
  );
}
